import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTimes } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { Instructions } from '../styled';
import CalendarSvg from 'shared/statics/svg/smiley-face.svg';
import styles from 'shared/auth/ui/style.module.scss';

export const passwordRegExPattern =
  '^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#$%^*&]).{12,60}$';
export const passwordRules =
  'minlength: 12; maxlength: 60; required: lower; required: upper; required: digit; required: [!#$%&*@^];';

export const checkPasswordValidations = (
  passwordValidations: boolean[],
  password: string,
  confirmPassword: string
): boolean =>
  passwordValidations.length === 0 ||
  passwordValidations.includes(false) ||
  password !== confirmPassword;

export const getPasswordValidations = (password: string): boolean[] => {
  const validations: boolean[] = [];
  validations[0] = /.{12,60}$/.test(password);
  validations[1] = /(?=.*[a-z])(?=.*[A-Z])/.test(password);
  validations[2] = /(?=.*?[0-9])/.test(password);
  validations[3] = /[!@#\$%^\*&]/.test(password);
  return validations;
};

const StyledDiv = styled.div`
  display: flex;
  gap: ${SPACERS.small};
  svg {
    padding: 4px ${SPACERS.tiny};
  }
  .error {
    color: ${COLORS.critical};
  }
  .success {
    color: ${COLORS.success};
  }
`;

interface InstructionRowProps {
  text: string;
  valid: boolean;
}

const InstructionRow: React.FC<InstructionRowProps> = ({ text, valid }) => {
  return (
    <StyledDiv>
      {valid ? (
        <FontAwesomeIcon icon={faCheck} className="success" />
      ) : (
        <FontAwesomeIcon icon={faTimes} className="error" />
      )}
      <span> {text}</span>
    </StyledDiv>
  );
};

const StyledCompletedInstructions = styled.div`
  text-align: center;
  img {
    padding-right: 6px;
  }
`;

const CompletedInstructions = () => (
  <Instructions className={`${styles.password_instructions} ${styles.active}`}>
    <StyledCompletedInstructions>
      <div>
        <img title="Completed" alt="Completed!" src={CalendarSvg} />
      </div>
      <div>
        <strong>Your password is officially awesome.</strong>
      </div>
      <div>Thanks for fulfilling all the requirements.</div>
    </StyledCompletedInstructions>
  </Instructions>
);

interface IncompleteInstructionsProps {
  validations: boolean[];
  className?: string;
}

const IncompleteInstructions: React.FC<IncompleteInstructionsProps> = ({
  validations
}) => {
  return (
    <Instructions
      className={`${styles.password_instructions} ${styles.active}`}
    >
      <span className={styles.intro}>
        <strong>
          Let’s make sure your password is nice and strong. Make sure your
          password:
        </strong>
      </span>
      <InstructionRow
        text="is at least 12 characters long"
        valid={validations[0]}
      />
      <InstructionRow
        text="contains at least one uppercase and one lowercase character"
        valid={validations[1]}
      />
      <InstructionRow
        text="contains at least one number"
        valid={validations[2]}
      />
      <InstructionRow
        text="contains at least one special character ($, &amp;, ^, #, etc.)"
        valid={validations[3]}
      />
    </Instructions>
  );
};

interface PasswordInstructionsProps {
  passwordValidations: boolean[];
  password: string;
  confirmPassword: string;
  incompleteClassName?: string;
}

const PasswordInstructions: React.FC<PasswordInstructionsProps> = ({
  password,
  confirmPassword,
  passwordValidations,
  incompleteClassName
}) => {
  if (
    checkPasswordValidations(passwordValidations, password, confirmPassword)
  ) {
    return (
      <IncompleteInstructions
        validations={passwordValidations}
        className={incompleteClassName}
      />
    );
  }
  return <CompletedInstructions />;
};

export default PasswordInstructions;
